import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Modal } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { FaTrash, FaPlus, FaEnvelope, FaPhone, FaEdit } from 'react-icons/fa';
import { getManagers, deleteManager, updateManager, addManager } from '../../services/managerService';
import { message, Modal as AntdModal } from 'antd'; // Add antd message and aliased Modal
import ManagerForm from './ManagerForm';
import { resolveManagerImageUrl } from './managerImageUrl';
import './Managers.scss';

const ManagerList = () => {
  const [managers, setManagers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedManager, setSelectedManager] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingManager, setEditingManager] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);

  useEffect(() => {
    fetchManagers();
  }, []);

  const fetchManagers = async () => {
    try {
      console.log('Fetching managers...');
      const response = await getManagers();
      console.log('Manager List Response:', response);
      
      if (Array.isArray(response)) {
        setManagers(response);
        setError(null);
      } else {
        console.log('Invalid response format:', response);
        setError('Müdürler yüklenirken bir hata oluştu.');
        setManagers([]);
      }
    } catch (err) {
      console.error('Fetch error:', err);
      setError('Müdürler yüklenirken bir hata oluştu.');
      setManagers([]);
    } finally {
      setLoading(false);
    }
  };

  const confirmDelete = (manager) => {
    AntdModal.confirm({
      title: 'Müdür Silme',
      content: (
        <div>
          Bu müdürü silmek istediğinizden emin misiniz?
          <br />
          <strong>{manager.name}</strong>
        </div>
      ),
      okText: 'Evet',
      okType: 'danger',
      cancelText: 'Hayır',
      onOk: async () => {
        try {
          const response = await deleteManager(manager.guid);
          if (response.isSuccess) {
            setManagers(prev => prev.filter(item => item.guid !== manager.guid));
            message.success('Müdür başarıyla silindi!');
          } else {
            message.error(response.resultMessage || 'Müdür silinirken bir hata oluştu.');
          }
        } catch (error) {
          console.error('Delete manager error:', error);
          message.error('Müdür silinirken bir hata oluştu.');
        }
      }
    });
  };

  const handleEdit = (manager) => {
    setEditingManager(manager);
    setShowEditModal(true);
  };

  const handleAdd = () => {
    setEditingManager(null);
    setShowAddModal(true);
  };

  const handleEditSubmit = async (updatedManager) => {
    try {
      // API'ye güncelleme isteği gönder
      const response = await updateManager(updatedManager.guid, updatedManager);
      
      console.log('Manager update response:', response);
      console.log('Response data:', response.data);
      console.log('Response status:', response.status);
      
      // Backend ServiceResult döndürüyor veya direkt true
      if ((response.data && response.data.isSuccess === true) || 
          response.data === true || 
          response.data === 'true' ||
          response.status === 200) {
        await fetchManagers();
        setShowEditModal(false);
        setEditingManager(null);
        message.success('Müdür başarıyla güncellendi!');
      } else {
        message.error(response.data?.resultMessage || 'Müdür güncellenirken bir hata oluştu.');
      }
    } catch (error) {
      console.error('Error updating manager:', error);
      message.error('Müdür güncellenirken bir hata oluştu.');
    }
  };

  const handleAddSubmit = async (newManager) => {
    try {
      const response = await addManager(newManager);
      if ((response.data && response.data.isSuccess === true) || 
          response.data === true || 
          response.data === 'true' ||
          response.status === 200) {
        await fetchManagers();
        setShowAddModal(false);
        message.success('Müdür başarıyla eklendi!');
      } else {
        message.error(response.data?.resultMessage || 'Müdür eklenirken bir hata oluştu.');
      }
    } catch (error) {
      console.error('Error adding manager:', error);
      message.error('Müdür eklenirken bir hata oluştu.');
    }
  };

  if (loading) {
    return (
      <Container>
        <div className="text-center mt-5">Yükleniyor...</div>
      </Container>
    );
  }

  return (
    <Container fluid className="managers-page">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>Müdürlerimiz</h2>
        <Button variant="primary" onClick={handleAdd}>
          <FaPlus className="me-2" />
          Yeni Müdür Ekle
        </Button>
      </div>

      {error ? (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      ) : managers.length === 0 ? (
        <div className="text-center mt-5">
          <p>Henüz müdür bulunmuyor.</p>
        </div>
      ) : (
        <Row>
          {managers.map((manager) => (
            <Col key={manager.guid} lg={3} md={6} xs={12} className="mb-4">
              <Card className="h-100 manager-card">
                <div className="manager-image-container">
                  <Card.Img 
                    variant="top" 
                    src={resolveManagerImageUrl(manager.pictureUrl)}
                    alt={manager.name || 'Müdür'}
                    className="manager-image"
                    onError={(e) => {
                      e.target.src = '/placeholder-manager.jpg';
                    }}
                  />
                </div>
                <Card.Body>
                  <Card.Title>{manager.name}</Card.Title>
                  <div className="manager-details">
                    <p className="position-name mb-3">{manager.positionName}</p>
                    
                    {(manager.phone || manager.email) && (
                      <div className="contact-info mb-3">
                        {manager.phone && (
                          <p className="mb-2">
                            <FaPhone className="me-2" />
                            {manager.phone}
                          </p>
                        )}
                        {manager.email && (
                          <p className="mb-2">
                            <FaEnvelope className="me-2" />
                            {manager.email}
                          </p>
                        )}
                      </div>
                    )}

                    {manager.mission && (
                      <div className="mb-2">
                        <strong>Misyon:</strong>
                        <p className="mission-text">{manager.mission}</p>
                      </div>
                    )}

                    {manager.vision && (
                      <div className="mb-2">
                        <strong>Vizyon:</strong>
                        <p className="vision-text">{manager.vision}</p>
                      </div>
                    )}
                  </div>
                </Card.Body>
                <Card.Footer className="bg-white">
                  <div className="d-flex gap-2">
                    <Button 
                      variant="outline-primary" 
                      size="sm"
                      onClick={() => handleEdit(manager)}
                    >
                      <FaEdit className="me-1" /> Düzenle
                    </Button>
                    <Button 
                      variant="outline-danger" 
                      size="sm"
                      onClick={() => confirmDelete(manager)}
                    >
                      <FaTrash className="me-1" /> Sil
                    </Button>
                  </div>
                </Card.Footer>
              </Card>
            </Col>
          ))}
        </Row>
      )}



      {/* Edit Modal */}
      <Modal show={showEditModal} onHide={() => setShowEditModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Müdür Düzenle</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <ManagerForm 
            editingManager={editingManager}
            onSubmit={handleEditSubmit}
            onCancel={() => setShowEditModal(false)}
            isModal={true}
          />
        </Modal.Body>
      </Modal>

      {/* Add Modal */}
      <Modal show={showAddModal} onHide={() => setShowAddModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Yeni Müdür Ekle</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <ManagerForm 
            editingManager={null}
            onSubmit={handleAddSubmit}
            onCancel={() => setShowAddModal(false)}
            isModal={true}
          />
        </Modal.Body>
      </Modal>


    </Container>
  );
};

export default ManagerList;
